import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { ListChildComponentProps } from 'react-window';
import {
  assignColumnAccessor,
  Column,
  IdArrayInput,
} from '../../hooks/useTablePages';
import {
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from './Table';
import { Checkbox } from '../Form';
import styled from '@emotion/styled';
import { Box } from 'rebass';
import useSize from '@react-hook/size';
import useScrollbarSize from '../../hooks/useScrollbarSize';
import TableColumn, { TableColumnProps } from './TableColumn';
import useRangeSelection, {
  preventShiftTextSelection,
} from '../../hooks/useRangeSelection';

import './TableWindow.css';

export interface DataTableProps<T = any> {
  columns: Column[];
  showHeader?: boolean;
  rowHeight?: number;
  itemIds?: (string | number)[] | readonly (string | number)[];
  items: T[];
  idField?: string;
  activeIds?: string[] | readonly string[];
  page?: string;
  mode?: string;
  history?: any;
  allowSelection?: boolean;
  onRowClick?: (item: T) => void;
  selectedIds?: readonly string[] | string[];
  onSelectionChange?: (selectedIds: readonly string[]) => void;
}

const noop = () => {};

const StyledTable = styled(Table)<{ maxWidth: number; borderWidth: number }>`
  display: flex;
  flex-direction: column;
  flex-grow: 0;

  .tbody {
    overflow-y: auto;
    overflow-x: hidden !important;
    flex-grow: 0;
    white-space: nowrap;
  }

  input[type='checkbox'] {
    margin: 0;
    padding: 0;
  }

  ${({ maxWidth, borderWidth }) =>
    maxWidth ? `max-width: ${maxWidth + borderWidth * 2}px` : ''}
`;

const StyledTableRow = styled(TableRow)``;

const StyledCheckbox = styled(Checkbox)<{ color?: string }>`
  position: relative;
  margin-top: -2px;
  margin-bottom: -3px;
  margin-left: -2px;
  width: 16px;
  height: 16px;
  ${({ color }) => (color ? `color: ${color} !important` : '')}
`;
type HeaderRowProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = {
  headerHeight: number;
  totalColumnsWidth: number;
  scrollBarSize: number;
  allowSelection?: boolean;
  selectAllClick?: () => void;
  selectedIds?: IdArrayInput<IdValue>;
  items: Item[];
  columns: Column[];
  onHeaderClick?: (column: Column) => void;
  checkboxColumnWidth: number;
};

const HeaderRow: React.FC<HeaderRowProps> = (props) => {
  const {
    headerHeight,
    totalColumnsWidth,
    scrollBarSize,
    allowSelection,
    selectAllClick,
    selectedIds,
    items,
    columns,
    onHeaderClick,
    checkboxColumnWidth,
  } = props;

  return (
    <TableHeaderRow
      style={{
        height: headerHeight,
        width: totalColumnsWidth + scrollBarSize,
      }}
      role="row"
    >
      {' '}
      {allowSelection && (
        <TableHeaderCell
          role="columnheader"
          onClick={selectAllClick}
          style={{ width: checkboxColumnWidth }}
        >
          <StyledCheckbox
            color="white"
            checked={items.length === selectedIds?.length}
            onChange={noop}
          />
        </TableHeaderCell>
      )}
      {columns.map((column, index) => {
        const id = column.id || (column.accessor as 'string');
        return (
          <TableHeaderCell
            key={id}
            style={{
              width: column.width,
            }}
            role="columnheader"
            onClick={() => (onHeaderClick ? onHeaderClick(column) : undefined)}
          >
            {column.header}
          </TableHeaderCell>
        );
      })}
    </TableHeaderRow>
  );
};

HeaderRow.propTypes = {
  headerHeight: PropTypes.any,
  totalColumnsWidth: PropTypes.any,
  allowSelection: PropTypes.any,
  selectAllClick: PropTypes.any,
  selectedIds: PropTypes.any,
  items: PropTypes.any,
  columns: PropTypes.any,
  onHeaderClick: PropTypes.any,
  checkboxColumnWidth: PropTypes.any,
};

const DEFAULT_ROW_HEIGHT = 32;
const INTERACTIVE_ELEMENT_SELECTOR =
  'button, .checkbox-cell, .checkbox-cell *, input, a, button *, a *';

const ItemRow: React.FC<ListChildComponentProps> = ({ data, index, style }) => {
  const {
    items,
    columns,
    idField,
    selectedIds,
    onSelectRow,
    onRowClick,
    rowHeight = DEFAULT_ROW_HEIGHT,
    allowSelection,
    checkboxColumnWidth,
    activeIds,
  } = data;
  const item = items[index];
  const itemId = item[idField];
  const selected = selectedIds ? selectedIds.indexOf(itemId) !== -1 : false;

  const selectRowClick = useCallback(
    (e: any) => {
      onSelectRow(index, e.shiftKey);
    },
    [onSelectRow, index]
  );

  const onClick = useCallback(
    (e: any) => {
      if (e.target.matches(INTERACTIVE_ELEMENT_SELECTOR)) {
        return;
      }
      if (onRowClick) {
        onRowClick(item);
      } else if (allowSelection) {
        selectRowClick(e);
      }
    },
    [selectRowClick, item, onRowClick, itemId]
  );

  const onMouseDown = useCallback((e: any) => {
    if (!e.target.matches(INTERACTIVE_ELEMENT_SELECTOR)) {
      preventShiftTextSelection(e);
    }
  }, []);

  return (
    <StyledTableRow
      key={'item-' + index}
      style={{ top: style.top, height: rowHeight }}
      role="row"
      onClick={onClick}
      onMouseDown={allowSelection && !onRowClick ? onMouseDown : undefined}
      className={activeIds && activeIds.includes(itemId) ? 'active' : undefined}
    >
      {allowSelection && (
        <TableCell
          key="checkbox"
          role="cell"
          className="checkbox-cell"
          style={{ width: checkboxColumnWidth }}
          onClick={selectRowClick}
          onMouseDown={preventShiftTextSelection}
        >
          <StyledCheckbox
            checked={selected}
            onChange={noop}
            title={selected ? 'Deselect' : 'Select'}
          />
        </TableCell>
      )}
      {item &&
        columns.map((column: Column) => (
          <TableColumn
            key={column.id}
            accessor={column.accessor as TableColumnProps['accessor']}
            width={column.width}
            item={item}
          />
        ))}
    </StyledTableRow>
  );
};

ItemRow.propTypes = {
  data: PropTypes.any,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
};

/**
 *
 * @component TableWindow that displays virtualized list of rows
 * Assumes that it always has a checkbox selection as the first column
 */
const DataTable: React.FC<DataTableProps> = ({
  columns,
  showHeader,
  rowHeight,
  items,
  idField = 'id',
  activeIds,
  allowSelection,
  onRowClick,
  selectedIds,
  onSelectionChange,
}: DataTableProps) => {
  useEffect(() => {
    columns.forEach(assignColumnAccessor);
  }, [columns]);

  const scrollBarSize = useScrollbarSize();
  const headerHeight = showHeader === false ? 0 : 36;
  const checkboxColumnWidth = 40;
  const borderWidth = 1;

  const totalColumnsWidth: number = useMemo(
    () =>
      columns.reduce<number>(
        (previousValue, currentValue) =>
          previousValue + (currentValue.width as number),
        allowSelection ? checkboxColumnWidth : 0
      ),
    [columns]
  );

  const itemIds = useMemo(() => {
    return items.map((item) => item[idField]);
  }, [items, idField]);

  const selectAllClick = useCallback(() => {
    if (onSelectionChange) {
      const allSelected = itemIds.length === selectedIds?.length;
      onSelectionChange(allSelected ? [] : itemIds);
    }
  }, [onSelectionChange, itemIds, selectedIds]);

  const onSelectRow = useRangeSelection(
    itemIds,
    selectedIds,
    onSelectionChange
  );

  columns.forEach(assignColumnAccessor);

  const [bodyWidth, setBodyWidth] = useState(totalColumnsWidth + scrollBarSize);

  const table = useRef<HTMLDivElement>(null);
  const [width] = useSize(table);

  const updateWidth = useCallback(() => {
    if (table.current) {
      const vScrollWidth =
        table.current.offsetWidth - borderWidth * 2 + table.current.scrollLeft;
      setBodyWidth(Math.min(totalColumnsWidth + scrollBarSize, vScrollWidth));
    }
  }, [totalColumnsWidth]);

  useEffect(updateWidth, [width, totalColumnsWidth]);

  useEffect(() => {
    table.current?.addEventListener('scroll', updateWidth);
    return () => {
      table.current?.removeEventListener('scroll', updateWidth);
    };
  }, [table.current, totalColumnsWidth]);

  return (
    <StyledTable
      ref={table}
      className="table"
      role="table"
      maxWidth={totalColumnsWidth + scrollBarSize}
      borderWidth={borderWidth}
    >
      <>
        {headerHeight > 0 && (
          <HeaderRow
            headerHeight={headerHeight}
            totalColumnsWidth={totalColumnsWidth}
            scrollBarSize={scrollBarSize}
            allowSelection={allowSelection}
            selectAllClick={selectAllClick}
            selectedIds={selectedIds}
            items={items}
            columns={columns}
            checkboxColumnWidth={checkboxColumnWidth}
          />
        )}
        <Box className="tbody" width={bodyWidth}>
          {items.map((value, index) => (
            <ItemRow
              key={index}
              index={index}
              style={{}}
              data={{
                items,
                columns,
                idField,
                selectedIds,
                onSelectRow,
                checkboxColumnWidth,
                rowHeight,
                allowSelection,
                onRowClick,
                activeIds,
                width: totalColumnsWidth,
              }}
            />
          ))}
        </Box>
      </>
    </StyledTable>
  );
};

export default DataTable;
